import type { ReactNode } from "react"
import { Rocket } from "lucide-react"
import { cn } from "@/lib/utils"

export interface DeployedStrategiesPageProps {
  heading: ReactNode
  description?: ReactNode
  bannerSlot?: ReactNode
  sectionHeading?: ReactNode
  summary?: ReactNode
  children?: ReactNode
  emptyState?: ReactNode
  defaultEmptyTitle?: string
  defaultEmptyBody?: string
  isEmpty?: boolean
  className?: string
}

export function DeployedStrategiesPage({
  heading,
  description,
  bannerSlot,
  sectionHeading,
  summary,
  children,
  emptyState,
  defaultEmptyTitle = "No versions deployed yet",
  defaultEmptyBody = "Switch back to Builder, iterate on your strategy, and hit Deploy to snapshot a version here.",
  isEmpty = false,
  className,
}: DeployedStrategiesPageProps) {
  const renderedEmpty = emptyState ?? (
    <div className="rounded-xl border border-border bg-card shadow-md p-10 flex flex-col items-center justify-center gap-3 text-center">
      <Rocket size={22} strokeWidth={1.5} className="text-muted-foreground" />
      <div className="text-body text-foreground">{defaultEmptyTitle}</div>
      <p className="text-caption text-muted-foreground max-w-[380px]">{defaultEmptyBody}</p>
    </div>
  )

  return (
    <div className={cn("h-full overflow-y-auto", className)}>
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 pt-5 sm:pt-8 pb-24 sm:pb-10 flex flex-col gap-6 sm:gap-7">
        <header className="flex flex-col gap-1.5">
          <h1 className="text-page-title text-foreground">{heading}</h1>
          {description && (
            <p className="text-body text-muted-foreground max-w-[640px]">{description}</p>
          )}
        </header>

        {bannerSlot}

        <section className="flex flex-col gap-4">
          {(sectionHeading || summary) && (
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              {sectionHeading && (
                <h2 className="text-section text-foreground">{sectionHeading}</h2>
              )}
              {summary && (
                <span className="text-caption text-muted-foreground tabular-nums">
                  {summary}
                </span>
              )}
            </div>
          )}

          {isEmpty ? renderedEmpty : <div className="flex flex-col gap-5">{children}</div>}
        </section>
      </div>
    </div>
  )
}
